import { useCallback, useRef } from 'react';
import {
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  Button,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { ImageUpload } from '@/components/ImageUpload';
import { usePostStoreMainBannerMutation } from '@/services/queries/store-main-banner';

export interface StoreBannersCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const StoreBannersCreateModal = ({ isOpen, onClose, onDone }: StoreBannersCreateModalProps) => {
  const toast = useToast();
  const { t } = useTranslation();

  const postItemMutation = usePostStoreMainBannerMutation();

  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      team_uuid: undefined,
      source_url: '',
      image: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: any = {
        title: data.title,
        source_url: data.source_url,
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.image,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_store.components.store_main_banners_create_modal.create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_store.components.store_main_banners_create_modal.create_error'),
          });
        },
      });
    },
    [postItemMutation, onDone, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_store.components.store_main_banners_create_modal.create_title')}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_store.components.store_main_banners_create_modal.create_team_label')}
              placeholder={t('pages.application_store.components.store_main_banners_create_modal.create_team_label')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <InputField
              withoutFloating
              label={t('pages.application_store.components.store_main_banners_create_modal.create_title_label')}
              {...register('title')}
              maxLength={100}
              rootProps={{
                isInvalid: !!errors?.title,
                errorMessage: errors?.title?.message,
              }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_store.components.store_main_banners_create_modal.create_link_label')}
              {...register('source_url')}
              maxLength={255}
              rootProps={{
                isInvalid: !!errors?.source_url,
                errorMessage: errors?.source_url?.message,
              }}
            />

            <ImageUpload
              imageURL={''}
              onFileChange={image => setValue('image', image)}
              error={errors?.image?.message?.toString()}
            />
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent='space-between' gap='kilo'>
          <Flex w='100%' justifyContent='space-between' alignItems='center' gap={2} paddingLeft={6}>
            <Flex w='100%' justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={handleClose}>
                {t('pages.application_store.components.store_main_banners_create_modal.create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_store.components.store_main_banners_create_modal.create_confirm')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
